<template>
  <AuthShell :title="t('auth.login')" :lead="t('auth.loginLead')" :background="background">
    <form class="auth-form" @submit.prevent="login">
      <StateMessage v-if="notice" :title="notice" tone="empty" />
      <StateMessage v-if="formError" :title="t('auth.errorTitle')" :message="formError" tone="error" />

      <LandingButton
        v-if="needsActivation"
        variant="ghost"
        block
        :to="{ name: 'Activate', query: { email } }"
      >
        {{ t('auth.activation.goToActivation') }}
      </LandingButton>

      <FormField
        v-model="email"
        type="email"
        :label="t('auth.email')"
        :placeholder="t('auth.enterEmail')"
        :error="fieldErrors.email"
        autocomplete="email"
        required
      />

      <FormField
        v-model="password"
        type="password"
        :label="t('auth.password')"
        :placeholder="t('auth.enterPassword')"
        :error="fieldErrors.password"
        :show-password-label="t('auth.showPassword')"
        :hide-password-label="t('auth.hidePassword')"
        autocomplete="current-password"
        required
      />

      <LandingButton type="submit" variant="accent" block :loading="isSubmitting">
        {{ isSubmitting ? t('auth.signingIn') : t('auth.login') }}
      </LandingButton>

      <p class="auth-form__separator"><span>{{ t('auth.orContinueWith') }}</span></p>

      <GoogleAuth mode="login" @success="onGoogleSuccess" @error="onGoogleError" />
    </form>

    <template #footer>
      <span class="auth__q">{{ t('auth.needAccountQ') }}</span>
      <router-link :to="{ name: 'Register' }">{{ t('auth.needAccountCta') }}</router-link>
    </template>
  </AuthShell>
</template>

<script setup>
import { computed, reactive, ref } from 'vue';
import axios from 'axios';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { useStore } from 'vuex';
import AuthShell from '@/components/public/AuthShell.vue';
import FormField from '@/components/public/FormField.vue';
import StateMessage from '@/components/public/StateMessage.vue';
import LandingButton from '@/components/btn/LandingButton.vue';
import GoogleAuth from '@/components/Auth/GoogleAuth.vue';
import background from '@/assets/images/backgrounds/auth-nave.webp';
import { safeRedirectTarget } from '@/utils/redirect.js';

const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const store = useStore();

const email = ref('');
const password = ref('');
const formError = ref('');
const fieldErrors = reactive({ email: '', password: '' });
const isSubmitting = ref(false);

/* Register redirects here with ?message=Registered; nothing ever read it.
 * Activate adds ?message=Activated when the link had already been spent. */
const NOTICE_KEYS = {
  Registered: 'auth.registerSuccess',
  Activated: 'auth.activation.alreadyActive',
  /* The session guard sends people here when their token dies mid-session. */
  SessionExpired: 'auth.sessionExpired',
};

const notice = computed(() => {
  const key = NOTICE_KEYS[route.query.message];
  return key ? t(key) : '';
});

/* Set when the account exists and the password was right, but the address was
 * never confirmed — the one failure with somewhere to go next. */
const needsActivation = ref(false);

const login = async () => {
  if (isSubmitting.value) return;
  isSubmitting.value = true;
  formError.value = '';
  needsActivation.value = false;
  fieldErrors.email = '';
  fieldErrors.password = '';

  try {
    const loginUrl = `${import.meta.env.VITE_API_BASE_URL}${import.meta.env.VITE_LOGIN_URL}`;
    const response = await axios.post(loginUrl, {
      email: email.value,
      password: password.value,
    });

    store.dispatch('login', { token: response.data.access });
    /*
     * The auth guard records where the visitor was headed before it bounced
     * them here, so a deep link survives the login instead of dumping everyone
     * on the dashboard. Only same-origin paths are honoured.
     */
    const safeRedirect = safeRedirectTarget(route.query.redirect);
    router.push(safeRedirect ?? { name: 'PlayerHomeDashboard' });
  } catch (error) {
    if (error.response?.data?.code === 'email_not_verified') {
      needsActivation.value = true;
      formError.value = t('auth.activation.blocked');
      /* 401 is what simplejwt answers for a bad email/password pair; it used to
       * fall through to the generic "something went wrong". */
    } else if ([400, 401].includes(error.response?.status)) {
      formError.value = t('auth.invalidCredentials');
    } else {
      formError.value = t('auth.unexpectedError');
    }
  } finally {
    isSubmitting.value = false;
  }
};

const onGoogleSuccess = () => {
  /* GoogleAuth dispatches to the store and redirects itself. */
};

const onGoogleError = ({ error }) => {
  formError.value = error;
};
</script>

<style scoped>
.auth-form {
  display: flex;
  flex-direction: column;
  gap: var(--space-5);
}

.auth-form__separator {
  display: flex;
  align-items: center;
  gap: var(--space-3);
  margin: 0;
  font-size: var(--text-sm);
  color: var(--text-muted);
}

.auth-form__separator::before,
.auth-form__separator::after {
  content: '';
  flex: 1;
  border-top: 1px solid var(--border);
}
</style>
